import React, { useState, useEffect, useRef } from 'react';
import { ProfessionStandardsDto } from '../../../common/Types';
import { Card, CardBody, Table, Form, FormGroup } from 'reactstrap';
import { Button, Input } from '../../../components';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { professionService } from '../../../services/ProfessionAdministration';
import { toast } from 'react-toastify';

type Props = {
    professionStandards: ProfessionStandardsDto[] | undefined;
    professionId: number;
};

interface Versions {
    versions: ProfessionStandardsDto[];
}

const collectFirstYupMessage = (errors: unknown): string | undefined => {
    if (!errors || typeof errors !== 'object') return undefined;
    if (Array.isArray(errors)) {
        for (const entry of errors) {
            const found = collectFirstYupMessage(entry);
            if (found) return found;
        }
        return undefined;
    }
    const message = (errors as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
    for (const value of Object.values(errors as Record<string, unknown>)) {
        const found = collectFirstYupMessage(value);
        if (found) return found;
    }
    return undefined;
};

const StandardVersionsCard = (props: Props) => {
    const [versions, setVersions] = useState<ProfessionStandardsDto[]>();
    const [editing, setEditing] = useState(false);
    const [deletedVersions, setDeletedVersions] = useState<ProfessionStandardsDto[]>([]);

    // Individual ProfessionStandardsDto validation schema
    const versionSchema = yup
        .object({
            professionStandardId: yup.number().required().default(0),
            professionId: yup.number().required(),
            standardYear: yup.number().required(),
            // Coerce NULL IsDeleted from legacy rows to false so they don't block submission.
            isDeleted: yup
                .boolean()
                .transform((value) => (value == null ? false : value))
                .default(false),
            // Audit fields are optional - handled by server
            dateCreated: yup.string().nullable().optional(),
            lastEdited: yup.string().nullable().optional(),
            createdByString: yup.string().nullable().optional(),
            editedByString: yup.string().nullable().optional(),
            createdBy: yup.string().nullable().optional(),
        })
        .defined();

    // ProfessionStandardsDto validation schema as a list
    const listSchema = yup.object().shape({
        versions: yup.array().of(versionSchema),
    });

    const { control, handleSubmit, reset, getValues } = useForm({
        resolver: yupResolver(listSchema as any),
        defaultValues: { versions: [] } as Versions,
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'versions',
    });

    // Track previous editing state to detect when we enter edit mode
    const prevEditingRef = useRef(false);

    // Initialize versions state when props change
    useEffect(() => {
        if (props.professionStandards) {
            setVersions(props.professionStandards);
        }
    }, [props.professionStandards]);

    // Reset form ONLY when entering edit mode (not when versions changes during editing)
    useEffect(() => {
        const wasEditing = prevEditingRef.current;
        prevEditingRef.current = editing;

        // Only reset when transitioning from not-editing to editing
        if (editing && !wasEditing && versions) {
            reset({ versions: [...versions] });
            setDeletedVersions([]);
        }
    }, [editing, versions, reset]);

    const onSubmit = (formData: Versions) => {
        const toastId = toast.info('Saving Standard Versions...');

        // Add back deleted standard versions
        const saveObj = [...formData.versions, ...deletedVersions];

        professionService
            .saveStandardVersions(saveObj)
            .then((response) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Standard Versions saved successfuly',
                });
                reset({ versions: response });
                setVersions(response);
                setEditing(false);
            })
            .catch(() =>
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving Standard Versions',
                }),
            );
    };

    const deleteVersion = (i: number) => {
        // Get the actual form data before removing
        const currentVersions = getValues('versions');
        const versionToDelete = currentVersions[i];

        // Existing standards require confirmation since deletion cannot be undone from the UI
        if (versionToDelete && versionToDelete.professionStandardId > 0) {
            const year = versionToDelete.standardYear || 'this';
            if (!window.confirm(`Are you sure you want to delete the ${year} standard version? This may affect recommendations that reference it.`)) {
                return;
            }
            setDeletedVersions([...deletedVersions, { ...versionToDelete, isDeleted: true }]);
        }

        remove(i);
    };

    return (
        <Card>
            <CardBody>
                <div style={{ display: 'flex' }} className="mb-2">
                    <div style={{ flex: 1 }}>
                        <h5>Standard Versions</h5>
                    </div>
                    {editing && (
                        <div>
                            <Button
                                type={'button'}
                                color={'info'}
                                onClick={() =>
                                    append({
                                        professionStandardId: 0,
                                        professionId: props.professionId,
                                        standardYear: new Date().getFullYear(),
                                        isDeleted: false,
                                    })
                                }
                            >
                                <i className="remixicon-add-line" />
                            </Button>
                        </div>
                    )}
                </div>
                {editing ? (
                    <Form noValidate onSubmit={handleSubmit(onSubmit, (errors) => {
                        console.error('Form validation errors:', errors);
                        const firstMessage = collectFirstYupMessage(errors);
                        toast.error(firstMessage ? `Validation error: ${firstMessage}` : 'Validation error - check console');
                    })}>
                        {fields.map((field, i) => {
                            return (
                                <div style={{ display: 'flex' }} key={field.id}>
                                    <FormGroup style={{ flex: 1, paddingRight: '8px' }}>
                                        <Controller
                                            name={`versions.${i}.standardYear`}
                                            control={control}
                                            render={({ field }) => (
                                                <Input type="text" {...field} value={field.value ?? ''} />
                                            )}
                                        />
                                        <Controller
                                            name={`versions.${i}.professionStandardId`}
                                            control={control}
                                            render={({ field }) => (
                                                <input type="hidden" {...field} value={field.value ?? ''} />
                                            )}
                                        />
                                        <Controller
                                            name={`versions.${i}.professionId`}
                                            control={control}
                                            render={({ field }) => (
                                                <input type="hidden" {...field} value={field.value ?? ''} />
                                            )}
                                        />
                                        <Controller
                                            name={`versions.${i}.createdBy`}
                                            control={control}
                                            render={({ field }) => (
                                                <input type="hidden" {...field} value={field.value ?? ''} />
                                            )}
                                        />
                                        <Controller
                                            name={`versions.${i}.dateCreated`}
                                            control={control}
                                            render={({ field }) => (
                                                <input type="hidden" {...field} value={field.value ?? ''} />
                                            )}
                                        />
                                        <Controller
                                            name={`versions.${i}.lastEdited`}
                                            control={control}
                                            render={({ field }) => (
                                                <input type="hidden" {...field} value={field.value ?? ''} />
                                            )}
                                        />
                                        <Controller
                                            name={`versions.${i}.isDeleted`}
                                            control={control}
                                            render={({ field }) => (
                                                <input
                                                    type="hidden"
                                                    {...field}
                                                    value={field.value ? 'true' : 'false'}
                                                />
                                            )}
                                        />
                                        <Controller
                                            name={`versions.${i}.createdByString`}
                                            control={control}
                                            render={({ field }) => (
                                                <input type="hidden" {...field} value={field.value ?? ''} />
                                            )}
                                        />
                                        <Controller
                                            name={`versions.${i}.editedByString`}
                                            control={control}
                                            render={({ field }) => (
                                                <input type="hidden" {...field} value={field.value ?? ''} />
                                            )}
                                        />
                                    </FormGroup>
                                    <div>
                                        <Button
                                            type={'button'}
                                            color={'danger'}
                                            onClick={() => deleteVersion(i)}
                                        >
                                            <i className={'remixicon-delete-bin-line'}></i>
                                        </Button>
                                    </div>
                                </div>
                            );
                        })}
                        <Button color="primary" type="submit">
                            Save
                        </Button>
                        &nbsp;
                        <Button outline onClick={() => setEditing(false)}>
                            Cancel
                        </Button>
                    </Form>
                ) : (
                    <>
                        <Table>
                            <tbody>
                                {versions?.map((standard) => {
                                    return (
                                        <tr key={standard.professionStandardId}>
                                            <td>{standard.standardYear}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                        <Button color="primary" onClick={() => setEditing(true)}>
                            Edit Versions
                        </Button>
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default StandardVersionsCard;
